import React, { useEffect, useState } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

interface MatchParams { id: string; }

const formatRating = (rating: any) => {
  if (rating == null || rating === '') return { text: 'Pending', color: '#6c757d', textColor: '#fff' };
  const str = String(rating).toUpperCase();
  if (str === 'READY') return { text: 'Ready', color: '#28a745', textColor: '#fff' };
  if (str === 'NOT_READY') return { text: 'Not Ready', color: '#dc3545', textColor: '#fff' };
  if (str === 'NEEDS_IMPROVEMENT') return { text: 'Needs Improvement', color: '#ffc107', textColor: '#212529' };
  const num = parseFloat(rating);
  if (!isNaN(num)) {
    const color = num > 7 ? '#28a745' : num > 4 ? '#ffc107' : '#dc3545';
    const textColor = (num > 4 && num <= 7) ? '#212529' : '#fff';
    return { text: `${num}/10`, color, textColor };
  }
  return { text: String(rating), color: '#6c757d', textColor: '#fff' };
};

const renderList = (data: any) => {
  if (!data) return null;
  if (typeof data === 'string') {
    return <p style={{ ...styles.secondaryText, whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>{data}</p>;
  }
  if (Array.isArray(data) && data.length > 0) {
    return (
      <ul style={{ margin: 0, paddingLeft: '20px' }}>
        {data.map((item: any, i: number) => (
          <li key={i} style={{ ...styles.secondaryText, marginBottom: '8px', lineHeight: '1.6' }}>
            {typeof item === 'string' ? item : (
              <div>
                {item.observation && <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>{item.observation}</div>}
                {item.note && <div style={{ color: '#555', lineHeight: '1.6' }}>{item.note}</div>}
                {item.advice && <div style={{ color: '#555', lineHeight: '1.6', fontStyle: 'italic' }}>{item.advice}</div>}
                {!item.observation && !item.note && !item.advice && JSON.stringify(item)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  }
  return <p style={styles.secondaryText}>None recorded.</p>;
};

const renderJsonBlock = (data: any) => {
  if (!data) return null;
  if (typeof data === 'string') return <p style={{ ...styles.secondaryText, whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>{data}</p>;
  return (
    <pre style={{ background: '#F8F9FA', border: '1px solid #E0E0E0', borderRadius: '10px', padding: '12px', maxHeight: '260px', overflow: 'auto', fontSize: '12px', color: '#333' }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

const renderTranscript = (transcript: any) => {
  if (!transcript) return null;

  if (typeof transcript === 'string') {
    return <p style={{ ...styles.secondaryText, whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{transcript}</p>;
  }

  if (Array.isArray(transcript)) {
    return (
      <div>
        {transcript.map((msg: any, i: number) => {
          const role = msg.role || msg.speaker || (msg.type === 'user' ? 'Candidate' : 'Interviewer');
          const content = msg.content || msg.text || msg.message || JSON.stringify(msg);
          const isCandidate = role.toLowerCase().includes('user') || role.toLowerCase().includes('candidate') || role.toLowerCase().includes('human');
          return (
            <div key={i} style={{
              marginBottom: '16px',
              padding: '12px 16px',
              borderRadius: '10px',
              background: isCandidate ? '#F0F4FF' : '#F8F9FA',
              borderLeft: `3px solid ${isCandidate ? '#0065FF' : '#E0E0E0'}`,
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: isCandidate ? '#0065FF' : '#6B7280', marginBottom: '4px', textTransform: 'uppercase' as any }}>
                {role}
              </div>
              <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.7' }}>{content}</div>
            </div>
          );
        })}
      </div>
    );
  }

  return <p style={styles.secondaryText}>{JSON.stringify(transcript)}</p>;
};

const InterviewResultDetail: React.FC<RouteComponentProps<MatchParams>> = ({ match }) => {
  const id = match.params.id;
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`${END_POINTS.interviewResults}${id}/`);
        setResult(r);
      } catch (e: any) { ValidationErrorToast('Error', e?.message || 'Failed to load result'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading result...</p>
        </div>
      </InterviewSidebar>
    );
  }

  if (!result) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <p style={styles.secondaryText}>Result not found.</p>
          <Link to="/interview-simulator/results" style={{ color: '#0065FF', textDecoration: 'none', marginTop: '16px', display: 'inline-block' }}>← Back to Results</Link>
        </div>
      </InterviewSidebar>
    );
  }

  const rt = formatRating(result.rating);
  const candidateName = result.candidateName || result.candidate_name || result.candidateEmail || result.candidate_email || 'Interview Result';
  const candidateEmail = result.candidateEmail || result.candidate_email || '';
  const jobTitle = result.jobTitle || result.job_title || '';
  const resumeUrl = result.resumeUrl || result.resume_url || result.resumeDataUrl || result.resume_data_url || result.resume_data;
  const resumeFileName = result.resumeFileName || result.resume_file_name || result.resumeName || result.resume_name;
  const resumeText = result.resumeText || result.resume_text;
  const linkedinUrl = result.linkedinUrl || result.linkedin_url || result.linkedInUrl || result.linkedInURL;
  const linkedinScrape = result.linkedinScrape || result.linkedin_scrape;

  return (
    <InterviewSidebar>
      <div style={{ marginBottom: '16px' }}>
        <Link to="/interview-simulator/results" style={{ color: '#0065FF', textDecoration: 'none', fontSize: '14px' }}>← Back to Results</Link>
      </div>

      {/* Header Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' as any, gap: '16px' }}>
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: '4px' }}>{candidateName}</h3>
            {candidateEmail && candidateEmail !== candidateName && (
              <p style={{ ...styles.secondaryText, margin: '0 0 4px' }}>{candidateEmail}</p>
            )}
            {jobTitle && <p style={{ ...styles.secondaryText, margin: 0, fontWeight: 500 }}>{jobTitle}</p>}
          </div>
          <span style={{
            ...styles.badge,
            backgroundColor: rt.color,
            color: rt.textColor,
            fontSize: '16px',
            padding: '8px 20px',
          }}>
            {rt.text}
          </span>
        </div>
      </div>

      {/* Candidate Materials */}
      {(resumeUrl || resumeFileName || resumeText || linkedinUrl || linkedinScrape) && (
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <h3 style={styles.cardTitle}>Candidate Materials</h3>
          <div className="row">
            <div className="col-md-6" style={{ marginBottom: '16px' }}>
              <h4 style={{ ...styles.primaryText, fontSize: '16px', marginBottom: '10px' }}>Resume</h4>
              {resumeFileName && <p style={styles.secondaryText}><strong>File:</strong> {resumeFileName}</p>}
              {resumeUrl && String(resumeUrl).startsWith('data:') ? (
                <a href={resumeUrl} download={resumeFileName || 'candidate-resume'} style={{ ...styles.button, ...styles.secondaryButton, textDecoration: 'none', marginBottom: '12px' } as any}>Download Resume</a>
              ) : resumeUrl ? (
                <a href={resumeUrl} target="_blank" rel="noopener noreferrer" style={{ ...styles.button, ...styles.secondaryButton, textDecoration: 'none', marginBottom: '12px' } as any}>Open Resume</a>
              ) : null}
              {resumeText && <p style={{ ...styles.secondaryText, whiteSpace: 'pre-wrap', lineHeight: '1.7', maxHeight: '220px', overflow: 'auto' }}>{String(resumeText).slice(0, 2000)}{String(resumeText).length > 2000 ? '…' : ''}</p>}
              {!resumeUrl && !resumeFileName && !resumeText && <p style={styles.secondaryText}>No resume returned by the API for this result.</p>}
            </div>
            <div className="col-md-6" style={{ marginBottom: '16px' }}>
              <h4 style={{ ...styles.primaryText, fontSize: '16px', marginBottom: '10px' }}>LinkedIn</h4>
              {linkedinUrl && <p style={styles.secondaryText}><strong>Profile:</strong> <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0065FF' }}>{linkedinUrl}</a></p>}
              {linkedinScrape ? renderJsonBlock(linkedinScrape) : <p style={styles.secondaryText}>No LinkedIn scrape payload returned by the API for this result.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {(result.summary || result.monologue || result.ai_analysis || result.aiAnalysis) && (
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <h3 style={styles.cardTitle}>Summary</h3>
          <p style={{ ...styles.secondaryText, whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
            {result.summary || result.monologue || result.ai_analysis || result.aiAnalysis}
          </p>
        </div>
      )}

      {/* Strengths & Weaknesses */}
      <div className="row" style={{ marginBottom: '24px' }}>
        <div className="col-md-6" style={{ marginBottom: '16px' }}>
          <div style={{ ...styles.card, height: '100%' }}>
            <h3 style={{ ...styles.cardTitle, color: '#28a745' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Strengths
            </h3>
            {renderList(result.strengths)}
          </div>
        </div>
        <div className="col-md-6" style={{ marginBottom: '16px' }}>
          <div style={{ ...styles.card, height: '100%' }}>
            <h3 style={{ ...styles.cardTitle, color: '#dc3545' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Areas for Improvement
            </h3>
            {renderList(result.weaknesses)}
          </div>
        </div>
      </div>

      {/* Transcript */}
      {result.transcript && (
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <h3 style={styles.cardTitle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            Interview Transcript
          </h3>
          <div style={{
            maxHeight: '600px',
            overflowY: 'auto' as any,
            padding: '16px',
            backgroundColor: '#FAFAFA',
            borderRadius: '10px',
            border: '1px solid #E0E0E0',
          }}>
            {renderTranscript(result.transcript)}
          </div>
        </div>
      )}
    </InterviewSidebar>
  );
};

export default withRouter(InterviewResultDetail);
